import React, { useState, useEffect } from 'react';
import {
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Box,
  Chip
} from '@mui/material';
import {
  FlightTakeoff,
  Map,
  Assignment,
  Folder,
  Settings
} from '@mui/icons-material';
import { useAuth } from '../../context/authContext';
import { useProfile } from '../../context/userContext';
import styles from '../../styles/IndividualToOrgUpgrade.module.css';

const DataSelection = ({ onSubmit, onCancel }) => {
  const { token } = useAuth();
  const { profile } = useProfile();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [dataStats, setDataStats] = useState({
    dronesCount: 0,
    missionsCount: 0,
    operationsCount: 0,
    filesCount: 0,
    filesSize: 0
  });

  const [selectedData, setSelectedData] = useState({
    drones: [],
    missions: [],
    operations: [],
    files: [],
    settings: true
  });

  const [selectAll, setSelectAll] = useState({
    drones: true,
    missions: true,
    operations: true,
    files: true
  });

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        // Fetch user's current data statistics
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/user/data-stats`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch data statistics');
        }

        const data = await response.json();
        setDataStats(data);

        // Pre-select all data for migration
        setSelectedData({
          drones: data.drones?.map(d => d.id) || [],
          missions: data.missions?.map(m => m.id) || [],
          operations: data.operations?.map(o => o.id) || [],
          files: data.files?.map(f => f.id) || [],
          settings: true
        });

        setLoading(false);
      } catch (error) {
        // Use mock data for development
        setDataStats({
          dronesCount: 3,
          missionsCount: 12,
          operationsCount: 45,
          filesCount: 28,
          filesSize: 1024 * 1024 * 250 // 250MB
        });
        setSelectedData({
          drones: [1, 2, 3],
          missions: Array.from({length: 12}, (_, i) => i + 1),
          operations: Array.from({length: 45}, (_, i) => i + 1),
          files: Array.from({length: 28}, (_, i) => i + 1),
          settings: true
        });
        setLoading(false);
      }
    };

    fetchUserData();
  }, [token]);

  const handleSelectAllToggle = (category) => {
    const newSelectAll = { ...selectAll, [category]: !selectAll[category] };
    setSelectAll(newSelectAll);

    if (newSelectAll[category]) {
      // Select all items in this category
      const allIds = Array.from(
        { length: dataStats[`${category}Count`] },
        (_, i) => i + 1
      );
      setSelectedData((prev) => ({ ...prev, [category]: allIds }));
    } else {
      // Deselect all items in this category
      setSelectedData((prev) => ({ ...prev, [category]: [] }));
    }
  };

  const handleSettingsToggle = () => {
    setSelectedData((prev) => ({ ...prev, settings: !prev.settings }));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const handleSubmit = () => {
    onSubmit(selectedData);
  };

  const migrationCategories = [
    {
      key: 'drones',
      title: 'Drones',
      icon: FlightTakeoff,
      count: dataStats.dronesCount,
      description: 'Your registered drones with maintenance logs and battery records',
      color: '#0066cc'
    },
    {
      key: 'missions',
      title: 'Missions',
      icon: Map,
      count: dataStats.missionsCount,
      description: 'Saved mission plans, flight paths, and geofence data',
      color: '#008000'
    },
    {
      key: 'operations',
      title: 'Flight Operations',
      icon: Assignment,
      count: dataStats.operationsCount,
      description: 'Flight logs, LAANC authorizations, and compliance records',
      color: '#9c27b0'
    },
    {
      key: 'files',
      title: 'Files',
      icon: Folder,
      count: dataStats.filesCount,
      extra: formatFileSize(dataStats.filesSize),
      description: 'Documents, images, and other uploaded files',
      color: '#ff6f00'
    }
  ];

  if (loading) {
    return (
      <div className={styles['loading-container']} role="status" aria-live="polite">
        <CircularProgress aria-label="Loading your data" />
        <span className="sr-only">Loading your data...</span>
      </div>
    );
  }

  const totalSelected =
    selectedData.drones.length +
    selectedData.missions.length +
    selectedData.operations.length +
    selectedData.files.length;

  return (
    <div className={styles['data-selection']}>
      <div className={styles['form-section']}>
        <h2 className={styles['section-title']}>
          Select Data to Migrate
        </h2>
        <p className={styles['section-description']}>
          Choose which data from your individual account you want to transfer to your new organization.
          You can select all or pick specific items.
        </p>

        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Alert severity="info" className={styles['migration-info']}>
          <strong>Note:</strong> Data migration is permanent. Your individual subscription will be
          canceled, and all selected data will be moved to the organization. Any unselected data
          will remain in your personal account but will be inaccessible after upgrade.
        </Alert>

        <div className={styles['categories-grid']}>
          {migrationCategories.map((category) => {
            const IconComponent = category.icon;
            const isSelected = selectAll[category.key];
            const selectedCount = selectedData[category.key].length;

            return (
              <Card
                key={category.key}
                className={`${styles['category-card']} ${isSelected ? styles['category-selected'] : ''}`}
                elevation={2}
              >
                <CardContent>
                  <div className={styles['category-header']}>
                    <div
                      className={styles['category-icon-wrapper']}
                      style={{ backgroundColor: `${category.color}15` }}
                      aria-hidden="true"
                    >
                      <IconComponent
                        className={styles['category-icon']}
                        style={{ color: category.color }}
                        fontSize="large"
                      />
                    </div>
                    <div className={styles['category-info']}>
                      <h3 className={styles['category-title']}>
                        {category.title}
                      </h3>
                      <div className={styles['category-counts']}>
                        <Chip
                          label={`${category.count} items`}
                          size="small"
                          className={styles['count-chip']}
                        />
                        {category.extra && (
                          <Chip
                            label={category.extra}
                            size="small"
                            className={styles['extra-chip']}
                          />
                        )}
                      </div>
                    </div>
                  </div>

                  <p className={styles['category-description']}>
                    {category.description}
                  </p>

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isSelected}
                        onChange={() => handleSelectAllToggle(category.key)}
                        color="primary"
                        disabled={category.count === 0}
                        inputProps={{
                          'aria-label': `Select all ${category.title.toLowerCase()}`
                        }}
                      />
                    }
                    label={
                      <span className={styles['checkbox-label']}>
                        {isSelected
                          ? `All selected (${selectedCount})`
                          : category.count > 0
                          ? 'Select all'
                          : 'No items available'}
                      </span>
                    }
                  />
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Account Settings */}
        <Card className={styles['settings-card']} elevation={2}>
          <CardContent>
            <div className={styles['settings-header']}>
              <div
                className={styles['category-icon-wrapper']}
                style={{ backgroundColor: '#d32f2f15' }}
                aria-hidden="true"
              >
                <Settings
                  className={styles['category-icon']}
                  style={{ color: '#d32f2f' }}
                  fontSize="large"
                />
              </div>
              <div className={styles['settings-info']}>
                <h3 className={styles['category-title']}>
                  Account Settings
                </h3>
                <p className={styles['settings-description']}>
                  Migrate your notification preferences, display settings, and profile information
                </p>
              </div>
            </div>

            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedData.settings}
                  onChange={handleSettingsToggle}
                  color="primary"
                  inputProps={{
                    'aria-label': 'Migrate account settings'
                  }}
                />
              }
              label={
                <span className={styles['checkbox-label']}>
                  Migrate account settings (recommended)
                </span>
              }
            />
          </CardContent>
        </Card>

        {/* Selection Summary */}
        <div className={styles['selection-summary']}>
          <h4 className={styles['summary-title']}>
            Migration Summary
          </h4>
          <p className={styles['summary-text']}>
            You've selected <strong>{totalSelected} items</strong> and
            <strong>{selectedData.settings ? ' account settings' : ' no settings'}</strong> to migrate
            to your organization.
          </p>
        </div>
      </div>

      <Box className={styles['form-actions']}>
        <Button
          variant="outlined"
          onClick={onCancel}
          aria-label="Cancel upgrade process"
          className={styles['btn-secondary']}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={totalSelected === 0 && !selectedData.settings}
          aria-label="Continue to organization setup"
          className={styles['btn-primary']}
        >
          Continue
        </Button>
      </Box>
    </div>
  );
};

export default DataSelection;
